import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, Loader2, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useBranding, buildBrandingUrl } from '../contexts/BrandingContext';
import { REG_STORAGE_KEY } from './RegisterPage';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_4dc508e2-00ad-4791-a42f-c0685c15cd50/artifacts/w7lm448x_background.png';

export default function RegisterCreateProfilePage() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const logoSrc = buildBrandingUrl(branding?.app_logo || branding?.logo_url);

  const [regData, setRegData] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Load registration state from localStorage
    const stored = localStorage.getItem(REG_STORAGE_KEY);
    if (!stored) {
      // No registration state — redirect back to Screen 1
      navigate('/register', { replace: true });
      return;
    }
    const parsed = JSON.parse(stored);
    setRegData(parsed);
    // Pre-fill name from Google/Apple if available
    if (parsed.name) setName(parsed.name);
    setTimeout(() => setMounted(true), 60);
  }, [navigate]);

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Please enter your full name'); return; }
    if (!password || password.length < 6) { toast.error('Password must be at least 6 characters'); return; }

    setLoading(true);
    try {
      const profileRes = await axios.post(`${API}/api/v1/mobile/auth/createProfile`, {
        name: name.trim(),
        email: regData.email,
        password,
        country_code: '1',
        company_id: 1,
      });

      const { accessToken: profileToken, survey, _id: userId } = profileRes.data;

      // Store survey data for the wizard
      const stored = JSON.parse(localStorage.getItem(REG_STORAGE_KEY) || '{}');
      localStorage.setItem(REG_STORAGE_KEY, JSON.stringify({
        ...stored,
        surveyId: survey?._id || null,
        profileToken: profileToken || null,
        userId: String(userId || stored.userId || ''),
      }));

      toast.success('Profile created! Let\'s personalise your plan.');
      navigate('/register/survey');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!regData) return null;

  return (
    <div
      data-testid="create-profile-page"
      style={{
        minHeight: '100vh',
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: '24px 16px',
        fontFamily: "'Nunito', 'Inter', sans-serif",
      }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,8,14,0.3)', pointerEvents: 'none' }} />

      <div
        data-testid="create-profile-card"
        style={{
          position: 'relative', zIndex: 1,
          width: '100%', maxWidth: 420,
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          borderRadius: 24,
          padding: '36px 32px 32px',
          boxShadow: '0 8px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,1)',
          border: '1px solid rgba(255,255,255,0.65)',
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        {/* Back button */}
        <button
          onClick={() => navigate('/register')}
          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 20 }}
        >
          <ChevronLeft size={16} /> Back
        </button>

        {/* Logo */}
        {logoSrc && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <img src={logoSrc} alt={branding?.brand_name || 'Logo'} style={{ height: 44, maxWidth: 130, objectFit: 'contain' }} />
          </div>
        )}

        {/* Heading */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <p style={{ color: '#f97316', fontSize: 18, fontWeight: 700, margin: '0 0 4px' }}>
            Hey there
          </p>
          <h1 style={{ color: '#1e3a8a', fontSize: 26, fontWeight: 900, margin: 0, letterSpacing: '-0.4px' }}>
            Create an Account
          </h1>
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Full Name */}
          <div style={{ position: 'relative' }}>
            <User size={18} color="#9ca3af" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              data-testid="profile-name-input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Full Name"
              autoComplete="name"
              style={fieldStyle}
              onFocus={e => { e.target.style.borderColor = '#1e3a8a'; e.target.style.boxShadow = '0 0 0 3px rgba(30,58,138,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          {/* Email (disabled) */}
          <div style={{ position: 'relative' }}>
            <Mail size={18} color="#9ca3af" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              data-testid="profile-email-input"
              type="email"
              value={regData.email}
              readOnly
              disabled
              style={{ ...fieldStyle, background: '#f5f7fa', color: '#6b7280', cursor: 'not-allowed' }}
            />
          </div>

          {/* Password */}
          <div style={{ position: 'relative' }}>
            <Lock size={18} color="#9ca3af" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              data-testid="profile-password-input"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              placeholder="Create Password"
              autoComplete="new-password"
              style={{ ...fieldStyle, paddingRight: 48 }}
              onFocus={e => { e.target.style.borderColor = '#1e3a8a'; e.target.style.boxShadow = '0 0 0 3px rgba(30,58,138,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              data-testid="toggle-password"
              style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 4 }}
            >
              {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>
          </div>

          {/* Register Button */}
          <button
            data-testid="register-submit-btn"
            onClick={handleSubmit}
            disabled={loading}
            style={{
              marginTop: 8,
              padding: '16px 0',
              background: loading ? '#6b7280' : '#1a2744',
              color: '#fff',
              border: 'none',
              borderRadius: 14,
              fontSize: 16,
              fontWeight: 800,
              letterSpacing: '0.3px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: loading ? 'none' : '0 4px 20px rgba(26,39,68,0.35)',
              transition: 'background 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#16213e'; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = '#1a2744'; }}
          >
            {loading
              ? <><Loader2 size={17} style={{ animation: 'spin 1s linear infinite' }} /> Creating account...</>
              : 'Register'
            }
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        input::placeholder { color: #9ca3af !important; }
      `}</style>
    </div>
  );
}

const fieldStyle = {
  width: '100%',
  padding: '14px 14px 14px 48px',
  background: '#f9fafb',
  border: '1.5px solid #e5e7eb',
  borderRadius: 12,
  color: '#111827',
  fontSize: 15,
  boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  outline: 'none',
};
